/** @format */

import {
	Avatar,
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Label from "../../label/Label";
import { colors } from "../../../utility/types/constant";
import { CircleInvitationItem } from "../../../redux/reducers/circleInvitationReducer";
import {
	getInvitationStatusMeta,
	isActionableInvitation,
	isAwaitingVerification,
	needsInviteeAction,
} from "./invitationStatus";

const FONT = "'Montserrat', sans-serif";
const DAY_MS = 24 * 60 * 60 * 1000;
const EXPIRING_SOON_MS = 2 * DAY_MS;

const fadeUp = keyframes`
	0% { opacity: 0; transform: translateY(12px); }
	100% { opacity: 1; transform: translateY(0); }
`;

const formatDate = (value?: string | null) => {
	if (!value) return "—";
	const date = new Date(value);
	if (isNaN(date.getTime())) return "—";
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
};

const describeExpiry = (expiresAt: string, status: string) => {
	const msLeft = new Date(expiresAt).getTime() - Date.now();
	if (isNaN(msLeft)) return { text: "", tone: "neutral" as const };
	const isPending = needsInviteeAction(status);
	// Accepted/denied invites are always past expiresAt eventually; only flag
	// expiry where it matters (same rule as the old table's red date).
	if (status === "EXPIRED" || (isPending && msLeft <= 0)) {
		return { text: "Expired", tone: "danger" as const };
	}
	if (!isPending) return { text: "", tone: "neutral" as const };
	if (msLeft <= DAY_MS) return { text: "Expires today", tone: "warning" as const };
	const days = Math.ceil(msLeft / DAY_MS);
	return {
		text: `in ${days} day${days === 1 ? "" : "s"}`,
		tone: msLeft <= EXPIRING_SOON_MS ? ("warning" as const) : ("neutral" as const),
	};
};

interface InvitationCardProps {
	invitation: CircleInvitationItem;
	index: number;
	isAcceptLoading: boolean;
	isDenyLoading: boolean;
	isBusy: boolean;
	isUserVerified: boolean;
	onAccept: (invitation: CircleInvitationItem) => void;
	onDeny: (invitation: CircleInvitationItem) => void;
	onOpenCircle: (circleId: string) => void;
	onVerifyIdentity: () => void;
}

export const InfoTile = ({
	icon,
	label,
	value,
	hint,
	hintColor,
}: {
	icon: React.ReactNode;
	label: string;
	value: React.ReactNode;
	hint?: string;
	hintColor?: string;
}) => (
	<Box
		sx={{
			minWidth: 0,
			p: 1.25,
			borderRadius: "12px",
			backgroundColor: "rgba(15, 23, 42, 0.035)",
		}}>
		<Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
			<Box sx={{ display: "flex", color: "text.disabled", "& svg": { fontSize: 16 } }}>
				{icon}
			</Box>
			<Typography
				sx={{
					fontFamily: FONT,
					fontSize: "0.68rem",
					fontWeight: 600,
					letterSpacing: "0.04em",
					textTransform: "uppercase",
					color: "text.secondary",
				}}>
				{label}
			</Typography>
		</Stack>
		<Typography
			noWrap
			sx={{
				fontFamily: FONT,
				fontSize: "0.9rem",
				fontWeight: 600,
				color: "text.primary",
			}}>
			{value}
		</Typography>
		{hint && (
			<Typography
				sx={{
					fontFamily: FONT,
					fontSize: "0.72rem",
					fontWeight: 600,
					color: hintColor ?? "text.secondary",
				}}>
				{hint}
			</Typography>
		)}
	</Box>
);

const InvitationCard = ({
	invitation,
	index,
	isAcceptLoading,
	isDenyLoading,
	isBusy,
	isUserVerified,
	onAccept,
	onDeny,
	onOpenCircle,
	onVerifyIdentity,
}: InvitationCardProps) => {
	const circle = invitation.circle;
	const circleName = circle?.circleName || "Unknown Circle";
	const memberCount = circle?.memberCount || 0;
	const statusMeta = getInvitationStatusMeta(invitation.status);
	const canAct = isActionableInvitation(invitation.status);
	const awaitingVerification = isAwaitingVerification(invitation.status);
	const canFinishJoining = awaitingVerification && isUserVerified;
	const needsAttention = canAct || awaitingVerification;
	const isMember = invitation.status === "COMPLETED_MEMBER_ADDED";
	const expiry = describeExpiry(invitation.expiresAt, invitation.status);
	const isExpiringSoon = needsAttention && expiry.tone === "warning";

	const expiryHintColor =
		expiry.tone === "danger" ? "error.main"
		: expiry.tone === "warning" ? colors.orangeWarning
		: "text.secondary";

	return (
		<Box
			component="article"
			aria-label={`Invitation to join ${circleName}`}
			sx={{
				position: "relative",
				display: "flex",
				flexDirection: "column",
				height: "100%",
				overflow: "hidden",
				borderRadius: "20px",
				backgroundColor: "#fff",
				border: "1px solid",
				borderColor: needsAttention ? "rgba(30, 193, 149, 0.45)" : "rgba(15, 23, 42, 0.08)",
				boxShadow:
					needsAttention ?
						"0 12px 32px rgba(2, 48, 32, 0.10)"
					:	"0 4px 16px rgba(15, 23, 42, 0.05)",
				animation: `${fadeUp} 0.45s ease-out both`,
				animationDelay: `${Math.min(index, 8) * 60}ms`,
				transition: "transform 0.2s ease, box-shadow 0.2s ease",
				"&:hover": {
					transform: { md: "translateY(-2px)" },
					boxShadow: "0 16px 36px rgba(15, 23, 42, 0.12)",
				},
				// Accent bar on cards that need a response
				"&::before":
					needsAttention ?
						{
							content: '""',
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							height: 4,
							background: `linear-gradient(90deg, ${colors.mainSecondary}, ${colors.blueRibbon})`,
						}
					:	{},
				"@media (prefers-reduced-motion: reduce)": {
					animation: "none",
					transition: "none",
					"&:hover": { transform: "none" },
				},
			}}>
			<Box sx={{ p: { xs: 2, sm: 2.5 }, pb: 2, flex: 1 }}>
				{/* Header: circle + status */}
				<Stack direction="row" spacing={1.75} alignItems="flex-start">
					<Avatar
						alt={circleName}
						src={circle?.circleImage || undefined}
						variant="rounded"
						sx={{
							width: 52,
							height: 52,
							borderRadius: "14px",
							fontFamily: FONT,
							fontWeight: 700,
							bgcolor: "rgba(20, 58, 162, 0.10)",
							color: colors.blueRibbon,
						}}>
						{circleName.charAt(0).toUpperCase()}
					</Avatar>

					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography
							component="h3"
							sx={{
								fontFamily: FONT,
								fontSize: "1.02rem",
								fontWeight: 700,
								lineHeight: 1.3,
								color: "text.primary",
								display: "-webkit-box",
								WebkitLineClamp: 2,
								WebkitBoxOrient: "vertical",
								overflow: "hidden",
								wordBreak: "break-word",
							}}>
							{circleName}
						</Typography>
						<Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
							<Label color={statusMeta.color}>{statusMeta.label}</Label>
							{isExpiringSoon && (
								<Label
									color="warning"
									variant="outlined"
									startIcon={<AccessTimeRoundedIcon />}>
									Expires soon
								</Label>
							)}
						</Box>
					</Box>
				</Stack>

				{/* Details: everything the old table showed */}
				<Box
					sx={{
						mt: 2,
						display: "grid",
						gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
						gap: 1,
					}}>
					<Box sx={{ gridColumn: "1 / -1" }}>
						<InfoTile
							icon={<PersonRoundedIcon />}
							label="Invited by"
							value={invitation.inviterName}
						/>
					</Box>
					<InfoTile
						icon={<GroupsRoundedIcon />}
						label="Members"
						value={memberCount}
					/>
					<InfoTile
						icon={<EventRoundedIcon />}
						label="Expires"
						value={formatDate(invitation.expiresAt)}
						hint={expiry.text}
						hintColor={expiryHintColor}
					/>
				</Box>
			</Box>

			{/* Footer: actions for pending invites, otherwise a status explanation */}
			<Box
				sx={{
					px: { xs: 2, sm: 2.5 },
					pb: { xs: 2, sm: 2.5 },
					pt: 0,
				}}>
				{canAct ?
					<Stack direction="row" spacing={1.25}>
						<Button
							fullWidth
							variant="outlined"
							disabled={isBusy}
							onClick={() => onDeny(invitation)}
							startIcon={isDenyLoading ? undefined : <CloseRoundedIcon />}
							aria-label={`Deny invitation to ${circleName}`}
							sx={{
								minHeight: 46,
								borderRadius: "12px",
								textTransform: "none",
								fontFamily: FONT,
								fontWeight: 700,
								color: colors.persianRed,
								borderColor: "rgba(202, 52, 51, 0.45)",
								"&:hover": {
									borderColor: colors.persianRed,
									backgroundColor: "rgba(202, 52, 51, 0.06)",
								},
							}}>
							{isDenyLoading ?
								<CircularProgress size={20} sx={{ color: colors.persianRed }} />
							:	"Deny"}
						</Button>
						<Button
							fullWidth
							variant="contained"
							disableElevation
							disabled={isBusy}
							onClick={() => onAccept(invitation)}
							startIcon={isAcceptLoading ? undefined : <CheckRoundedIcon />}
							aria-label={`Accept invitation to ${circleName}`}
							sx={{
								minHeight: 46,
								borderRadius: "12px",
								textTransform: "none",
								fontFamily: FONT,
								fontWeight: 700,
								color: "#fff",
								backgroundColor: colors.secondPrimary,
								"&:hover": { backgroundColor: "#03462f" },
							}}>
							{isAcceptLoading ?
								<CircularProgress size={20} sx={{ color: "#fff" }} />
							:	"Accept"}
						</Button>
					</Stack>
				:	<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={1.25}
						alignItems={{ xs: "stretch", sm: "center" }}
						sx={{
							p: 1.5,
							borderRadius: "12px",
							backgroundColor: "rgba(15, 23, 42, 0.03)",
						}}>
						<Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flex: 1 }}>
							<InfoOutlinedIcon sx={{ fontSize: 18, mt: "2px", color: "text.secondary" }} />
							<Typography
								sx={{
									fontFamily: FONT,
									fontSize: "0.82rem",
									color: "text.secondary",
									lineHeight: 1.45,
								}}>
								{canFinishJoining ?
									"You're verified. Finish joining to complete your membership."
								:	statusMeta.helperText}
							</Typography>
						</Stack>
						{canFinishJoining && (
							<Button
								variant="contained"
								disableElevation
								disabled={isBusy}
								onClick={() => onAccept(invitation)}
								aria-label={`Finish joining ${circleName}`}
								sx={{
									flexShrink: 0,
									minHeight: 44,
									px: 2.25,
									borderRadius: "10px",
									textTransform: "none",
									fontFamily: FONT,
									fontWeight: 700,
									color: "#fff",
									backgroundColor: colors.secondPrimary,
									"&:hover": { backgroundColor: "#03462f" },
								}}>
								{isAcceptLoading ?
									<CircularProgress size={20} sx={{ color: "#fff" }} />
								:	"Finish joining"}
							</Button>
						)}
						{awaitingVerification && !isUserVerified && (
							<Button
								size="small"
								onClick={onVerifyIdentity}
								endIcon={<ArrowForwardRoundedIcon />}
								sx={{
									flexShrink: 0,
									minHeight: 40,
									borderRadius: "10px",
									textTransform: "none",
									fontFamily: FONT,
									fontWeight: 700,
									color: colors.blueRibbon,
								}}>
								Verify identity
							</Button>
						)}
						{isMember && (
							<Button
								size="small"
								onClick={() => onOpenCircle(invitation.circleId)}
								endIcon={<ArrowForwardRoundedIcon />}
								sx={{
									flexShrink: 0,
									minHeight: 40,
									borderRadius: "10px",
									textTransform: "none",
									fontFamily: FONT,
									fontWeight: 700,
									color: colors.blueRibbon,
								}}>
								Open circle
							</Button>
						)}
					</Stack>
				}
			</Box>
		</Box>
	);
};

export default InvitationCard;
